import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronDown, ChevronRight, Copy, Check, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { fmtDate } from "@/features/power_projects/utils";
import { useAuthStore } from "@/store/auth";
import { useCarryOverRepeatMatch, useReviewRepeatMatch, useZERepeatMatches } from "../hooks";
import type { ZECarryOverResult, ZETenderRepeatMatch } from "../types";

/** Werdykt AI → etykieta. "" = AI nie było pytane (za słabe przesłanki albo brak klucza). */
const VERDICT_LABEL: Record<string, string> = {
  same: "AI: to ten sam przetarg",
  unclear: "AI: nie rozstrzyga",
  different: "AI: inne postępowanie",
};

const CONFIDENCE_LABEL: Record<string, string> = {
  high: "wysoka pewność",
  medium: "średnia pewność",
  low: "niska pewność",
};

const isActive = (m: ZETenderRepeatMatch) =>
  m.status !== "dismissed" && m.verdict !== "different";

/** „Ten przetarg już raz był i został anulowany" — baner nad przetargiem ZE.
 *
 *  Pokazuje przesłanki (nazwa, wspólne działki, odległość, wspólne nazwy plików) i
 *  werdykt AI, a przede
 *  wszystkim daje jeden przycisk: zaciągnij stamtąd trasę i wycenę zamiast rysować od
 *  nowa. Dopasowania odrzucone i uznane przez AI za inne postępowanie chowamy pod
 *  „pokaż odrzucone" — znikać bez śladu nie mogą, bo pierwsze pytanie brzmi „czemu to
 *  zniknęło". */
export function ZERepeatMatchBanner({
  tenderId,
  onImported,
}: {
  tenderId: string;
  onImported?: (result: ZECarryOverResult) => void;
}) {
  const { data: matches, isLoading } = useZERepeatMatches(tenderId);
  const [showRejected, setShowRejected] = useState(false);

  const active = useMemo(() => (matches ?? []).filter(isActive), [matches]);
  const rejected = useMemo(() => (matches ?? []).filter((m) => !isActive(m)), [matches]);

  if (isLoading || !matches?.length) return null;

  return (
    <div className="mb-4 space-y-2">
      {active.map((match) => (
        <RepeatCard key={match.id} tenderId={tenderId} match={match} onImported={onImported} />
      ))}

      {rejected.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowRejected((v) => !v)}
            className="flex items-center gap-1 text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
          >
            {showRejected ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            Odrzucone dopasowania ({rejected.length})
          </button>
          {showRejected &&
            rejected.map((match) => (
              <RepeatCard
                key={match.id}
                tenderId={tenderId}
                match={match}
                onImported={onImported}
                muted
              />
            ))}
        </div>
      )}
    </div>
  );
}

function RepeatCard({
  tenderId,
  match,
  onImported,
  muted = false,
}: {
  tenderId: string;
  match: ZETenderRepeatMatch;
  onImported?: (result: ZECarryOverResult) => void;
  muted?: boolean;
}) {
  const canCalculatePricing = useAuthStore(
    (s) => !!s.user?.has_calculate_pricing_permission,
  );
  const review = useReviewRepeatMatch(tenderId);
  const carryOver = useCarryOverRepeatMatch(tenderId);

  const drawableVariants = match.previous_variants.filter((v) => v.has_drawing);
  const [variantId, setVariantId] = useState(drawableVariants[0]?.id ?? "");
  const [wantRoute, setWantRoute] = useState(true);
  // Wycena tylko dla osób w obiegu wyceny — bez uprawnienia backend i tak odmówi.
  const [wantPricing, setWantPricing] = useState(canCalculatePricing);
  const [error, setError] = useState<string | null>(null);

  const hasPricing = !!match.previous_pricing;
  const hasSomethingToCopy = drawableVariants.length > 0 || hasPricing;
  const routeChecked = wantRoute && drawableVariants.length > 0;
  const pricingChecked = wantPricing && hasPricing && canCalculatePricing;

  const tone = muted
    ? "border-[var(--color-border)] bg-[var(--color-bg-subtle)]"
    : "border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700";

  const doCarryOver = () => {
    setError(null);
    carryOver.mutate(
      {
        matchId: match.id,
        route: routeChecked,
        pricing: pricingChecked,
        variant: routeChecked ? variantId || undefined : undefined,
      },
      {
        onSuccess: (result) => onImported?.(result),
        onError: (e: unknown) => {
          const detail = (e as { response?: { data?: { errors?: unknown } } })?.response?.data
            ?.errors;
          setError(
            Array.isArray(detail) ? String(detail[0]) : "Nie udało się zaciągnąć danych.",
          );
        },
      },
    );
  };

  return (
    <div className={`rounded border p-3 ${tone}`}>
      <div className="flex items-start gap-2">
        {!muted && (
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
        )}
        <div className="min-w-0 flex-1 space-y-2">
          <div>
            <p
              className={`text-xs font-semibold ${
                muted
                  ? "text-[var(--color-text-secondary)]"
                  : "text-amber-800 dark:text-amber-300"
              }`}
            >
              {match.status === "confirmed"
                ? "Potwierdzona powtórka anulowanego przetargu"
                : "Ten przetarg wygląda na powtórkę anulowanego"}
              {match.imported_at && " · dane już zaciągnięte"}
            </p>
            <p className="mt-0.5 text-sm text-[var(--color-text-primary)]">
              <Link
                to={`/ze-tenders/${match.previous}`}
                className="underline decoration-dotted underline-offset-2 hover:text-[var(--color-primary)]"
              >
                {match.previous_title}
              </Link>{" "}
              <span className="text-[11px] text-[var(--color-text-muted)]">
                ({match.previous_status}
                {match.previous_deadline ? `, termin ${fmtDate(match.previous_deadline)}` : ""})
              </span>
            </p>
          </div>

          {/* Przesłanki — dlaczego system uważa, że to to samo. */}
          <div className="flex flex-wrap items-center gap-1.5">
            {match.reasons.map((reason) => (
              <span
                key={reason}
                className="rounded-full bg-[var(--color-bg-base)] px-2 py-0.5 text-[11px] text-[var(--color-text-secondary)] border border-[var(--color-border)]"
              >
                {reason}
              </span>
            ))}
            <span className="text-[11px] text-[var(--color-text-muted)]">
              zgodność {Math.round(match.score * 100)}%
            </span>
          </div>

          {match.shared_teryts.length > 0 && (
            <p className="text-[11px] font-mono text-[var(--color-text-muted)]">
              Wspólne działki: {match.shared_teryts.slice(0, 6).join(", ")}
              {match.shared_teryts.length > 6 && ` … (+${match.shared_teryts.length - 6})`}
            </p>
          )}

          {match.shared_file_names.length > 0 && (
            <p className="text-[11px] text-[var(--color-text-muted)]">
              Wspólne pliki: {match.shared_file_names.slice(0, 4).join(", ")}
              {match.shared_files > 4 && ` … (+${match.shared_files - 4})`}
            </p>
          )}

          {match.verdict && (
            <p className="text-[11px] text-[var(--color-text-secondary)]">
              <strong>{VERDICT_LABEL[match.verdict] ?? match.verdict}</strong>
              {match.ai_confidence && ` (${CONFIDENCE_LABEL[match.ai_confidence] ?? match.ai_confidence})`}
              {match.ai_reasoning && ` — ${match.ai_reasoning}`}
            </p>
          )}

          {/* Co da się zaciągnąć. */}
          {hasSomethingToCopy ? (
            <div className="flex flex-wrap items-center gap-3 pt-1">
              <label className="flex items-center gap-1.5 text-[11px] text-[var(--color-text-secondary)]">
                <input
                  type="checkbox"
                  checked={routeChecked}
                  disabled={drawableVariants.length === 0}
                  onChange={(e) => setWantRoute(e.target.checked)}
                />
                Trasa i działki
                {drawableVariants.length === 0 && " (brak rysunku)"}
              </label>

              {drawableVariants.length > 1 && routeChecked && (
                <select
                  value={variantId}
                  onChange={(e) => setVariantId(e.target.value)}
                  className="rounded border border-[var(--color-border)] bg-[var(--color-bg-base)] px-1.5 py-0.5 text-[11px]"
                >
                  {drawableVariants.map((v) => (
                    <option key={v.id} value={v.id}>
                      Wariant {v.label} ({v.lines} lin. / {v.objects} ob. / {v.parcels} dz.)
                    </option>
                  ))}
                </select>
              )}

              <label
                className="flex items-center gap-1.5 text-[11px] text-[var(--color-text-secondary)]"
                title={
                  canCalculatePricing
                    ? undefined
                    : "Zaciągnięcie wyceny wymaga uprawnienia do wyliczania wycen"
                }
              >
                <input
                  type="checkbox"
                  checked={pricingChecked}
                  disabled={!hasPricing || !canCalculatePricing}
                  onChange={(e) => setWantPricing(e.target.checked)}
                />
                Wycena
                {!hasPricing
                  ? " (brak)"
                  : match.previous_pricing?.value
                    ? ` (${match.previous_pricing.items} poz., ${match.previous_pricing.value} zł)`
                    : ` (${match.previous_pricing?.items} poz.)`}
              </label>

              <Button
                size="sm"
                onClick={doCarryOver}
                disabled={carryOver.isPending || (!routeChecked && !pricingChecked)}
              >
                {carryOver.isPending ? (
                  <Spinner className="h-3 w-3" />
                ) : (
                  <Copy size={13} className="mr-1" />
                )}
                {match.imported_at ? "Zaciągnij ponownie" : "Zaciągnij z anulowanego"}
              </Button>
            </div>
          ) : (
            <p className="text-[11px] text-[var(--color-text-muted)]">
              W tamtym przetargu nie ma ani narysowanej trasy, ani zapisanej wyceny — nie ma
              czego zaciągnąć.
            </p>
          )}

          {error && <p className="text-[11px] text-[var(--color-danger)]">{error}</p>}

          {/* Werdykt człowieka. */}
          <div className="flex items-center gap-2 pt-0.5">
            {match.status !== "confirmed" && (
              <button
                type="button"
                onClick={() => review.mutate({ matchId: match.id, status: "confirmed" })}
                disabled={review.isPending}
                className="flex items-center gap-1 text-[11px] text-[var(--color-text-secondary)] hover:text-[var(--color-primary)]"
              >
                <Check size={12} /> To ta sama sprawa
              </button>
            )}
            {match.status !== "dismissed" ? (
              <button
                type="button"
                onClick={() => review.mutate({ matchId: match.id, status: "dismissed" })}
                disabled={review.isPending}
                className="flex items-center gap-1 text-[11px] text-[var(--color-text-secondary)] hover:text-[var(--color-danger)]"
              >
                <X size={12} /> To nie to samo
              </button>
            ) : (
              <button
                type="button"
                onClick={() => review.mutate({ matchId: match.id, status: "proposed" })}
                disabled={review.isPending}
                className="flex items-center gap-1 text-[11px] text-[var(--color-text-secondary)] hover:text-[var(--color-primary)]"
              >
                Przywróć
              </button>
            )}
            {match.reviewed_by_name && (
              <span className="text-[11px] text-[var(--color-text-muted)]">
                {match.status === "dismissed" ? "Odrzucił" : "Potwierdził"}:{" "}
                {match.reviewed_by_name}
                {match.reviewed_at && `, ${fmtDate(match.reviewed_at)}`}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
